import { useState, useRef } from 'react';
import { simulateStreaming, TEST_MARKDOWN_SAMPLES, findMarkdownBoundaries } from '../lib/testStreamingRenderer';
import { renderLLMMarkdown } from './ChatSession';
import { renderMarkdown } from '../markdown/markdown-factory';
import { prepareMarkdown } from '../markdown/utils';
import { blunt } from '../tools';
import '../styles/StreamingTest.css';

/**
 * Test component for debugging streaming markdown rendering
 *
 * Add to your app temporarily:
 * import StreamingTest from './components/StreamingTest';
 * <StreamingTest />
 *
 * Or access via browser console:
 * window.__streamingTest = { start: () => {...}, stop: () => {...} }
 */
export const StreamingTest = () => {
  const [markdown, setMarkdown] = useState('');
  const [selectedSample, setSelectedSample] = useState('complex');
  const [intervalMs, setIntervalMs] = useState(50);
  const [isStreaming, setIsStreaming] = useState(false);
  const [position, setPosition] = useState(0);
  const [totalLength, setTotalLength] = useState(0);
  const [errors, setErrors] = useState([]);
  const [useFixLLMMarkdown, setUseFixLLMMarkdown] = useState(true);
  const controllerRef = useRef(null);

  const startStreaming = () => {
    const fullText = TEST_MARKDOWN_SAMPLES[selectedSample];
    setTotalLength(fullText.length);
    setErrors([]);
    setIsStreaming(true);

    const boundaries = findMarkdownBoundaries(fullText);
    console.log(`Starting stream of ${fullText.length} characters with ${boundaries.length} markdown boundaries`);

    controllerRef.current = simulateStreaming(fullText, {
      intervalMs,
      onUpdate: (partial, index) => {
        setMarkdown(partial);
        setPosition(index);

        // Check if we're at a markdown boundary
        const boundary = boundaries.find(b => b.position === index - 1);
        if (boundary) {
          console.log(`Boundary at ${index}: ${boundary.type} - "${boundary.text}"`);
        }
      },
      onComplete: (final) => {
        console.log('Streaming complete');
        setIsStreaming(false);
        setMarkdown(final);
      },
      onError: (error, partial, index) => {
        console.error(`Rendering error at position ${index}:`, error);
        setErrors(prev => [...prev, {
          position: index,
          error: error.message,
          partial: partial.substring(Math.max(0, index - 50), index + 50)
        }]);
        setIsStreaming(false);
      }
    });
  };

  const stopStreaming = () => {
    if (controllerRef.current) {
      controllerRef.current.stop();
      setIsStreaming(false);
    }
  };

  const resetTest = () => {
    stopStreaming();
    setMarkdown('');
    setPosition(0);
    setErrors([]);
  };

  return (
    <div className="streaming-test">
      <div className="streaming-test-controls">
        <h3>Streaming Markdown Test</h3>

        <div className="control-group">
          <label>Sample:</label>
          <select
            value={selectedSample}
            onChange={(e) => setSelectedSample(e.target.value)}
            disabled={isStreaming}
          >
            {Object.keys(TEST_MARKDOWN_SAMPLES).map(key => (
              <option key={key} value={key}>{key}</option>
            ))}
          </select>
        </div>

        <div className="control-group">
          <label>Interval (ms):</label>
          <input
            type="number"
            value={intervalMs}
            onChange={(e) => setIntervalMs(parseInt(e.target.value))}
            min="1"
            max="1000"
            disabled={isStreaming}
          />
        </div>

        <div className="control-group">
          <label>
            <input
              type="checkbox"
              checked={useFixLLMMarkdown}
              onChange={(e) => setUseFixLLMMarkdown(e.target.checked)}
            />
            Apply fixLLMMarkdown preprocessing
          </label>
        </div>

        <div className="control-group">
          <button onClick={startStreaming} disabled={isStreaming}>
            Start Streaming
          </button>
          <button onClick={stopStreaming} disabled={!isStreaming}>
            Stop
          </button>
          <button onClick={resetTest} disabled={isStreaming}>
            Reset
          </button>
        </div>

        <div className="progress">
          {totalLength > 0 && (
            <div>
              Progress: {position} / {totalLength} ({Math.round(position / totalLength * 100)}%)
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${position / totalLength * 100}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {errors.length > 0 && (
          <div className="errors">
            <h4>Render Errors ({errors.length}):</h4>
            {errors.map((err, idx) => (
              <div key={idx} className="error-entry">
                <strong>Position {err.position}:</strong> {err.error}
                <pre>{err.partial}</pre>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="streaming-test-output">
        <h4>Rendered Output:</h4>
        <p className="streaming-note">
          Note: With <code>optimizeForStreaming</code>, incomplete markdown structures are suppressed
          until complete. This prevents errors but may cause content to "pop in" rather than stream smoothly.
        </p>
        <div className="rendered-markdown">
          {markdown && (useFixLLMMarkdown
            ? renderLLMMarkdown(markdown, true)
            : renderMarkdown({ markdown: prepareMarkdown(blunt(markdown)), trapErrors: true })
          )}
        </div>

        <h4>Raw Markdown:</h4>
        <textarea
          className="raw-markdown"
          value={markdown}
          onChange={(e) => setMarkdown(e.target.value)}
          placeholder="Enter markdown here or use streaming controls above..."
        />
      </div>
    </div>
  );
};

export default StreamingTest;